"use client";

import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Top-level error boundary for the /game canvas. A render crash inside the
 * Canvas2D engine (or any child) shows a recoverable, brand-styled message
 * instead of a blank white screen. "Try again" resets the boundary so React
 * remounts the subtree; "Reload" does a full page reload as a last resort.
 */
export class GameErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : "Unknown render error",
    };
  }

  componentDidCatch(error: unknown) {
    // Surface the crash in the console for debugging; the UI stays recoverable.
    console.error("[GameErrorBoundary] render crash:", error);
  }

  private handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center"
        style={{ backgroundColor: "var(--cream)", color: "var(--ink)" }}
      >
        <h2 className="text-2xl font-extrabold" style={{ color: "var(--ink)" }}>
          The world hit a snag
        </h2>
        <p className="max-w-md text-sm" style={{ color: "var(--ink-soft)" }}>
          Something broke while rendering the game. Your progress is saved — you
          can try again without losing your spot.
        </p>
        <p
          className="max-w-md break-words font-mono text-xs"
          style={{ color: "var(--ink-soft)" }}
        >
          {this.state.message}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={this.handleReset}
            className="rounded-lg px-5 py-2 text-sm font-bold"
            style={{ backgroundColor: "var(--gold)", color: "var(--ink)" }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border px-5 py-2 text-sm font-bold"
            style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default GameErrorBoundary;
